import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import {
  Avatar as _Avatar,
  Flex as _Flex,
  Box as _Box,
  Link as _Link,
  Tooltip as _Tooltip,
  Icon as _Icon,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@heroicons/react/solid';

// Chakra 2.10 + TypeScript 4.9 trip TS2590 ("union type too complex") when
// style-prop-heavy components are composed in a single JSX tree. Collapsing
// the components to React.ComponentType<any> kills the inference cascade.
// Runtime behavior is unchanged; Chakra style-prop IntelliSense is suppressed
// in this file only.
type CC = React.ComponentType<any>;
const Avatar = _Avatar as CC;
const Flex = _Flex as CC;
const Box = _Box as CC;
const Link = _Link as CC;
const Tooltip = _Tooltip as CC;
const Icon = _Icon as CC;

import { FormBuilder } from '@/components/formBuilder';
import { SelectOptionWithAvatar } from '@/components/formBuilder/SelectComponents';
import { ScorecardThresholdField } from '@/components/ats/scorecard-threshold-selector';
import { useInterviewStageManager } from '../../../../stage-manager';
import { selectUsers } from '@/store/users/selectors';
import { getAssessments, AssessmentsResponse, Assessment } from './api';
import { KTag, KText, KAvatarItem } from '@/ui-library/components';
import { useShowToast } from '@/hooks/useShowToast';
import { useKFlags } from '@/hooks/use-k-flags';
import { PlatformLogical } from '@/types/ext_assessment';
import { FeaturedIcon } from '@/assets/icons/ats';
import { KPopover } from '@/ui-library/components/organisms/KPopover';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';

const PLATFORM_CONFIG = {
  hackerrank: {
    name: 'HackerRank',
    apiEndpoint: 'hacker_rank',
  },
  hackerearth: {
    name: 'HackerEarth',
    apiEndpoint: 'hacker_earth',
  },
  criteriacorp: {
    name: 'Criteria Corp',
    apiEndpoint: 'criteriacorp',
  },
  hireflix: {
    name: 'Hireflix',
    apiEndpoint: 'hireflix',
  },
  codeinterview: {
    name: 'CodeInterview',
    apiEndpoint: 'code_interview',
  },
};

const COMMON_FIELDS = {
  testIdField: 'ext_test_id',
  testNameField: 'ext_test_name',
  testLinkField: 'ext_assessment_link',
};

const getDetailsInitialValue = (
  activity: any,
  _stage: any,
  _platform: PlatformLogical
) => {
  if (!activity?.id) {
    return undefined;
  }

  const testId = activity?.[COMMON_FIELDS.testIdField];
  const testName = activity?.[COMMON_FIELDS.testNameField];
  const testLink = activity?.[COMMON_FIELDS.testLinkField];
  const { participants = [], participant_groups = [] } = activity || {};

  const initialTestValue =
    testId && testName ? { value: testId, label: testName } : null;

  return {
    [COMMON_FIELDS.testIdField]: initialTestValue,
    [COMMON_FIELDS.testNameField]: testName || '',
    [COMMON_FIELDS.testLinkField]: testLink || '',
    reviewer_ids: [
      ...participants.map((item: any) => item?.id),
      ...(participant_groups?.length > 0 ? participant_groups : []),
    ],
    scorecard_completion_threshold:
      activity?.scorecard_completion_threshold ?? null,
  };
};

interface DetailsProps {
  formRef?: any;
  activity?: any;
  jobStage?: boolean;
  platform: PlatformLogical;
  invalidApplications?: any[];
  isBulk?: boolean;
  positions?: any[];
  onExistingInterviewApplicationsChange?: (_applications: any[]) => void;
  onExtTestIdChange?: (_testId: string | null) => void;
  validationBanner?: React.ReactNode;
}

export const Details: React.FC<DetailsProps> = ({
  formRef = null,
  activity = {},
  jobStage = false,
  platform,
  invalidApplications = [],
  isBulk = false,
  positions = [],
  onExistingInterviewApplicationsChange,
  onExtTestIdChange,
  validationBanner,
}) => {
  const { feScorecardThreshold } = useKFlags();
  const containerRef = useRef(null);
  const errorShownRef = useRef<any>(null);
  const toast = useShowToast();
  const { activityAction } = useInterviewStageManager();
  const { stage = {} } = activityAction as any;

  const config = PLATFORM_CONFIG[platform];
  const { status, [COMMON_FIELDS.testLinkField]: assessmentLink } =
    activity || {};

  const [selectedAssessment, setSelectedAssessment] =
    useState<Assessment | null>(null);
  const [existingInterviewApplications, setExistingInterviewApplications] =
    useState<any[]>([]);

  const users = useSelector(selectUsers);
  const usersOptions = users?.map((user) => ({
    ...user,
    label: user?.name,
    value: user?.id,
    avatar: user?.avatar,
  }));

  const labelStyles = { fontSize: '14px', fontWeight: 'semiBold' };
  const containerStyles = { paddingLeft: '24px', paddingRight: '24px' };

  const { data: assessments, isLoading: isLoadingAssessments } =
    useQuery<AssessmentsResponse>({
      queryKey: [platform, 'assessments'],
      queryFn: () => getAssessments(platform),
      staleTime: 0,
      enabled:
        activity?.status !== 'external_invited' &&
        activity?.status !== 'external_completed',
    });

  useEffect(() => {
    if (assessments?.error && errorShownRef.current !== assessments.error) {
      const errorMessage =
        assessments.error === 'err_hackerearth_authentication_failed'
          ? 'HackerEarth re-authentication by an admin required for this action.'
          : assessments.details;

      toast({
        title: errorMessage,
        status: 'error',
      });

      errorShownRef.current = assessments.error;
    }
  }, [assessments?.error, assessments?.details, toast]);

  const assessmentOptions =
    assessments?.tests?.map((assessment: any) => ({
      label: assessment.name || assessment.title,
      value: assessment.id,
    })) || [];

  const isAssessmentLocked =
    status === 'external_completed' || status === 'external_invited';
  const disableAssessmentSelect = isLoadingAssessments || isAssessmentLocked;

  const handleAssessmentChange = (selectedValue: any) => {
    const selected = assessments?.tests?.find(
      (assessment: any) => assessment.id === selectedValue.value
    ) as (Assessment & { title?: string }) | undefined;

    setSelectedAssessment(selected || null);

    if (selected && formRef.current) {
      const assessmentName = selected.name || selected.title;
      formRef.current.setFieldValue(COMMON_FIELDS.testIdField, selectedValue);
      formRef.current.setFieldValue(
        COMMON_FIELDS.testNameField,
        assessmentName
      );
      formRef.current.setFieldValue(COMMON_FIELDS.testLinkField, selected.link);
    }

    // Notify parent of test ID change for validation
    onExtTestIdChange?.(selectedValue?.value || null);

    if (isBulk && platform === 'hireflix' && positions?.length > 0) {
      const selectedPosition = positions.find(
        (pos: any) => pos.id === selectedValue.value
      );
      const existingApps =
        selectedPosition?.existing_interview_applications || [];
      setExistingInterviewApplications(existingApps);
      onExistingInterviewApplicationsChange?.(existingApps);
    }
  };

  // Check if we should auto-select the single available assessment
  const shouldAutoSelect =
    assessments?.tests?.length === 1 &&
    !isLoadingAssessments &&
    !isAssessmentLocked &&
    !activity?.id;

  const initialValues = useMemo(() => {
    const baseInitialValues = getDetailsInitialValue(activity, stage, platform);

    if (!shouldAutoSelect) {
      return baseInitialValues;
    }

    const singleAssessment = assessments.tests[0];
    const assessmentName = singleAssessment.name || singleAssessment.title;
    return {
      ...baseInitialValues,
      [COMMON_FIELDS.testIdField]: {
        label: assessmentName,
        value: singleAssessment.id,
      },
      [COMMON_FIELDS.testNameField]: assessmentName,
      [COMMON_FIELDS.testLinkField]: singleAssessment.link || '',
    };
  }, [shouldAutoSelect, assessments?.tests, activity, stage, platform]);

  useEffect(() => {
    if (!shouldAutoSelect) return;

    const singleAssessment = assessments.tests[0];
    setSelectedAssessment(singleAssessment as Assessment);

    // Notify parent of test ID for validation
    onExtTestIdChange?.(
      singleAssessment.id !== undefined && singleAssessment.id !== null
        ? String(singleAssessment.id)
        : null
    );

    // Handle bulk Hireflix existing applications
    if (isBulk && platform === 'hireflix' && positions?.length > 0) {
      const selectedPosition = positions.find(
        (pos: any) => pos.id === singleAssessment.id
      );
      if (selectedPosition?.existing_interview_applications?.length > 0) {
        const existingApps = selectedPosition.existing_interview_applications;
        setExistingInterviewApplications(existingApps);
        onExistingInterviewApplicationsChange?.(existingApps);
      }
    }
  }, [
    shouldAutoSelect,
    assessments?.tests,
    isBulk,
    platform,
    positions,
    onExistingInterviewApplicationsChange,
    onExtTestIdChange,
  ]);

  const DisabledAssessmentSelect = ({ formik }: { formik: any }) => {
    const currentValue = formik?.values?.[COMMON_FIELDS.testIdField];
    const displayValue =
      currentValue?.label || currentValue?.value || 'Select your assessment';

    return (
      <Tooltip
        label={`Once an assessment is sent on ${config.name}, it can't be changed. To send a different assessment, cancel this one and send again.`}
        placement="bottom"
        hasArrow
      >
        <Box px="24px">
          <KText size="sm-semi-bold" color="secondary-900" mb="8px">
            Select assessment{' '}
            <KText as="span" color="error-200" display="inline-block">
              *
            </KText>
          </KText>
          <Box
            minH="44px"
            borderRadius="6px"
            border="1px solid"
            borderColor="secondary-100"
            bg="secondary-25"
            px="8px"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            cursor="not-allowed"
          >
            <KText size="sm-medium" color="secondary-500">
              {displayValue}
            </KText>
            <Icon
              as={ChevronDownIcon}
              color="secondary-500"
              w="20px"
              h="20px"
            />
          </Box>
        </Box>
      </Tooltip>
    );
  };

  const renderCandidatesTag = (candidate: any, index: number) => {
    const {
      first_name = '',
      last_name = '',
      image_url,
    } = candidate?.contact || {};
    const name = `${first_name} ${last_name}`.trim();

    return (
      <Flex
        key={index}
        bg="white"
        p="6px 8px"
        borderRadius="6px"
        alignItems="center"
        gap="6px"
      >
        <Avatar
          className="avatar-xs"
          name={name}
          w="20px"
          h="20px"
          borderRadius="3px"
          src={image_url}
        />
        <TruncatedTextWithTooltip
          size="sm-medium"
          text={name}
          maxW="120px"
          enableTooltip
        />
      </Flex>
    );
  };

  const renderInvalidCandidatesTag = ({
    candidates,
    bgColor = 'pastel-1-light',
    color = 'pastel-1-dark',
    context = "don't have an associated email address and will not receive the Hireflix link",
  }: {
    candidates: any[];
    bgColor?: string;
    color?: string;
    context?: string;
  }) => {
    const VISIBLE_COUNT = 3;
    const visibleCandidates = candidates.slice(0, VISIBLE_COUNT);
    const hiddenCandidates = candidates.slice(VISIBLE_COUNT);
    const remainingCount = hiddenCandidates.length;

    const renderPlusMoreTrigger = () => (
      <Flex
        bg="white"
        width="33px"
        height="33px"
        alignItems="center"
        justifyContent="center"
        borderRadius="6px"
        border="1px solid #fff"
        cursor="pointer"
      >
        <KText color="secondary-700" fontWeight="550" fontSize="12px">
          +{remainingCount}
        </KText>
      </Flex>
    );

    const candidateCountText =
      candidates.length === 1
        ? '1 candidate'
        : `${candidates.length} candidates`;

    return (
      <Flex
        bg={bgColor}
        borderRadius="sm"
        maxW="100%"
        p="16px"
        flexDir="column"
        mx="24px"
        mt="8px"
        mb="16px"
      >
        <Flex gap="11px">
          <Icon as={FeaturedIcon} w="38px" h="38px" color={color} />
          <Flex flexDir="column" gap="8px" width="calc(100% - 49px)">
            <KText color={color}>
              <strong>{candidateCountText}</strong> {context}
            </KText>
            <Flex gap="6px" width="100%" flexWrap="nowrap">
              {visibleCandidates.map(renderCandidatesTag)}
              {remainingCount > 0 && (
                <KPopover
                  popoverProps={{ trigger: 'hover' }}
                  triggerElement={renderPlusMoreTrigger()}
                  showCloseButton={false}
                  popoverContentProps={{
                    p: '0',
                    maxHeight: '340px',
                    overflow: 'scroll',
                  }}
                  popoverBodyProps={{ p: '0px' }}
                >
                  <>
                    {hiddenCandidates.map((candidate: any, idx: number) => {
                      const {
                        first_name = '',
                        last_name = '',
                        image_url,
                      } = candidate?.contact || {};
                      const name = `${first_name} ${last_name}`.trim();

                      return (
                        <KAvatarItem
                          key={idx}
                          user={candidate}
                          name={name}
                          src={image_url || null}
                          showName
                          avatarProps={{
                            className: 'avatar-xs',
                            width: '24px',
                            height: '24px',
                            borderRadius: '6px',
                            border: '1px solid #fff',
                          }}
                          containerProps={{
                            p: '10px',
                            borderBottom: '1px solid #E9EBF4',
                            alignItems: 'center',
                            gap: '12px',
                          }}
                          size="sm"
                          lineHeight="1rem"
                        />
                      );
                    })}
                  </>
                </KPopover>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    );
  };

  const jobRoleOptions = [
    {
      label: 'Coordinators (on job)',
      value: 'job_coordinator',
      hideAvatar: true,
    },
    { label: 'Recruiters (on job)', value: 'job_recruiter', hideAvatar: true },
    {
      label: 'Hiring Managers (on job)',
      value: 'job_hiring_manager',
      hideAvatar: true,
    },
  ];

  const reviewerOptions = !jobStage
    ? [...jobRoleOptions, ...usersOptions]
    : usersOptions;

  const LoadingAssessmentNote = () => (
    <Box px="24px" pt="12px" mt="4" mb="4">
      <KTag
        p="12px 16px"
        value={
          <KText display="inline">
            <KText display="inline" size="sm-medium" color="secondary-900">
              Note:
            </KText>{' '}
            we're loading all available assessments from your {config.name}{' '}
            account. This might take a few seconds. Please hang tight.
          </KText>
        }
      />
    </Box>
  );

  const formConfig = [
    {
      name: 'reviewer_ids',
      label: 'Add reviewer(s)',
      type: 'autoComplete',
      isRequired: true,
      isMulti: true,
      useInput: true,
      updateEndpointOnChange: true,
      width: '100%',
      containerStyles,
      labelStyles,
      customFormatLabel: SelectOptionWithAvatar,
      loadDefaultOptions: true,
      options: reviewerOptions,
      fetchConfig: {
        method: 'get',
        endPoint: `account/users/search?filter_by[status][]=active&filter_value=`,
        processResponse: (res: any) =>
          res?.data?.users
            ?.map((item: any) => ({
              label: `${item?.first_name} ${item?.last_name}`,
              value: item?.id,
              avatar: item?.avatar_thumb_url,
              ...item,
            }))
            .concat(!jobStage ? jobRoleOptions : []),
        httpType: 'v1',
      },
      placeholder: 'Select reviewer',
      errorMessage: 'Reviewer(s) is required',
    },
    ...(feScorecardThreshold
      ? [
          {
            name: 'scorecard_completion_threshold',
            type: 'custom',
            Field: (props: { formik: any }) => (
              <ScorecardThresholdField
                {...props}
                useCriteriaCorpSpacing={platform === 'criteriacorp'}
                containerStyles={{
                  ml: '24px',
                }}
              />
            ),
          },
        ]
      : []),
    disableAssessmentSelect
      ? {
          type: 'custom',
          Field: DisabledAssessmentSelect,
        }
      : {
          name: COMMON_FIELDS.testIdField,
          label: 'Select assessment',
          placeholder: isLoadingAssessments
            ? `Loading assessments from ${config.name}...`
            : 'Select your assessment',
          type: 'kSelect',
          isRequired: true,
          useInput: true,
          width: '100%',
          validationType: 'object',
          containerStyles,
          disabled: disableAssessmentSelect,
          labelStyles,
          options: assessmentOptions,
          isLoading: isLoadingAssessments,
          customOnChange: handleAssessmentChange,
        },
    ...(isLoadingAssessments
      ? [
          {
            type: 'custom',
            name: 'loading_assessment',
            Field: LoadingAssessmentNote,
          },
        ]
      : []),
  ];

  const assessmentPreviewLink = selectedAssessment?.link || assessmentLink;
  const showExistingInterviewWarning =
    isBulk &&
    platform === 'hireflix' &&
    existingInterviewApplications?.length > 0;
  const showInvalidCandidatesWarning =
    isBulk && platform === 'hireflix' && invalidApplications?.length > 0;

  return (
    <Flex height="100vh" flexDir="column" ref={containerRef}>
      <FormBuilder
        config={formConfig}
        formRef={formRef}
        initialValues={initialValues}
        showCustomError={false}
      />

      {assessmentPreviewLink && (
        <Flex p="24px" flexDir="column" gap="8px">
          <KText size="sm-medium" color="secondary-900">
            View Assessment
          </KText>
          <Link
            href={assessmentPreviewLink}
            target="_blank"
            w="100%"
            display="flex"
            alignItems="center"
            gap="6px"
          >
            <KText
              size="sm-medium"
              color="primary-500"
              _hover={{
                textDecoration: 'underline',
                color: 'primary-500',
                cursor: 'pointer',
              }}
            >
              {assessmentPreviewLink}
            </KText>
          </Link>
        </Flex>
      )}

      {showExistingInterviewWarning &&
        renderInvalidCandidatesTag({
          candidates: existingInterviewApplications,
          bgColor: 'pastel-6-light',
          color: 'pastel-6-dark',
          context: 'skipped - already invited or completed this interview',
        })}

      {showInvalidCandidatesWarning &&
        renderInvalidCandidatesTag({
          candidates: invalidApplications,
          bgColor: 'pastel-1-light',
          color: 'pastel-1-dark',
          context:
            "don't have an associated email address and will not receive the Hireflix link",
        })}

      {/* Bulk action validation banner from parent */}
      {validationBanner}
    </Flex>
  );
};
